import { PlusIcon } from "lucide-react";
import { useState } from "react";

import { getDonorIds } from "@blis/shared/models/subject";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { SubjectCreateDialog } from "@/features/subjects/components/SubjectCreateDialog";
import type { TSubjectDetail } from "@/lib/types";
import { format } from "@/lib/utils";
import { useDialogProps } from "../detail/state";
import { SubjectSelect } from "../form-new/components/SubjectSelect";

export function MoveToSubjectDialog({
  accession,
}: {
  accession: { id: number; subjectId: number; subject: TSubjectDetail };
}) {
  const dialogProps = useDialogProps("moveToSubject");
  const [selectedSubject, setSelectedSubject] = useState<
    TSubjectDetail | undefined
  >();
  const [createOpen, setCreateOpen] = useState(false);
  const [startingValue, setStartingValue] = useState<{
    value: string;
    type: "caseId" | "unosId";
  } | null>(null);

  const moveToSubject = trpc.accession.moveToSubject.useMutation({
    onSuccess: (updated, variables) => {
      setSelectedSubject(undefined);
      setStartingValue(null);
      setCreateOpen(false);
      dialogProps.onOpenChange(false);
      toast.success(`Accession moved to ${updated.caseId}`);
    },
    onError: (err) => {
      toast.error("Error moving accession", {
        description: err.message,
      });
    },
  });

  const currentSubject = accession.subject;
  const canSubmit =
    selectedSubject !== undefined && selectedSubject.id !== currentSubject.id;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedSubject(undefined);
      setStartingValue(null);
      setCreateOpen(false);
    }
    dialogProps.onOpenChange(open);
  };

  return (
    <Modal isOpen={dialogProps.open} onOpenChange={handleOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Move accession to donor</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <DonorSummary label="Current donor" subject={currentSubject} />
              <DonorSummary
                label="Destination donor"
                subject={selectedSubject}
                placeholder="No destination selected"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <Label>Move to donor</Label>
                <SubjectCreateDialog
                  open={createOpen}
                  onOpenChange={setCreateOpen}
                  startingValue={startingValue}
                  onAlreadyExists={(subject) => {
                    setCreateOpen(false);
                    setSelectedSubject(subject);
                    toast.success(
                      "Donor already exists, selected existing donor",
                    );
                  }}
                  onCreated={setSelectedSubject}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onPress={() => setCreateOpen(true)}
                  >
                    <PlusIcon />
                    New donor
                  </Button>
                </SubjectCreateDialog>
              </div>
              <Text elementType="p" muted>
                Select an existing donor or create the destination donor.
              </Text>
              <SubjectSelect
                value={selectedSubject}
                buttonClassName="w-full"
                placeholder="Select destination donor..."
                searchPlaceholder="Search donor ID, UNOS ID, or alternate ID..."
                onValueChanged={setSelectedSubject}
                onCreateSelected={(val) => {
                  setStartingValue(val);
                  setCreateOpen(true);
                }}
              />
              {selectedSubject?.id === currentSubject.id && (
                <Text elementType="p" size="sm" isDanger>
                  Select a different donor to move this accession.
                </Text>
              )}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            isPending={moveToSubject.isPending}
            isDisabled={!canSubmit || moveToSubject.isPending}
            onPress={() => {
              if (!selectedSubject) return;
              moveToSubject.mutate({
                accessionId: accession.id,
                subjectId: selectedSubject.id,
              });
            }}
          >
            Move accession
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

function DonorSummary({
  label,
  subject,
  placeholder,
}: {
  label: string;
  subject?: TSubjectDetail;
  placeholder?: string;
}) {
  const details = subject ? getSubjectDetails(subject) : [];

  return (
    <div className="rounded-md border bg-background p-3">
      <Text elementType="div" size="xs" muted>
        {label}
      </Text>
      {subject ? (
        <div className="mt-1 flex min-w-0 flex-col gap-2">
          <Text elementType="div" bold truncate>
            {subject.caseId}
          </Text>
          {details.length > 0 && (
            <dl className="grid grid-cols-2 gap-2">
              {details.map((detail) => (
                <div key={detail.label} className="min-w-0">
                  <dt className="text-xs text-muted-foreground/80">
                    {detail.label}
                  </dt>
                  <dd className="truncate text-sm font-medium">
                    {detail.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ) : (
        <Text elementType="div" className="mt-1" muted truncate>
          {placeholder}
        </Text>
      )}
    </div>
  );
}

function getSubjectDetails(subject: TSubjectDetail) {
  const ids = getDonorIds(subject);
  return [
    ids.unosId ? { label: "UNOS", value: ids.unosId } : null,
    ids.donorIds.length > 0
      ? { label: "Alternate IDs", value: ids.donorIds.join(", ") }
      : null,
    subject.dob
      ? { label: "DOB", value: format(subject.dob, { time: false }) }
      : null,
    subject.sex ? { label: "Sex", value: subject.sex } : null,
  ].filter((detail) => !!detail);
}
